import Router from 'next/router';
import isEqual from 'lodash/isEqual';
import sortBy from 'lodash/sortBy';
import isServer from '@framework/router/Next/isServer';
import { getQueryString } from '@framework/router';
import { addQueryParams } from '@framework/router/nextRouter';
import Product from '@/creditflowresearch/domain/Product';
import Region from '@/creditflowresearch/domain/Region';
import { IProduct } from '@/creditflowresearch/domain/vo/Product';
import { IRegion } from '@/creditflowresearch/domain/vo/Region';
import { IUsePageFiltersState } from '@/creditflowresearch/application/PageFiltersState/usePageFiltersState';
import { validateUrlFilter } from './validateUrlFilter';

interface IProductRegionFilters {
  productIds?: string[],
  regionIds?: string[],
  queryParam?: string,
}

export const initProductRegionFilters = (
  productsCollection: IProduct[],
  regionsCollection: IRegion[],
  shouldSetFilterFromHeader: boolean,
  routerQuery: Record<string, string | string[]> | undefined,
  pageFiltersState: IUsePageFiltersState,
): boolean => {
  const { state } = pageFiltersState;
  const query = routerQuery || Router.query || {};

  // Parse ?products...&regions=... URL
  const urlProducts = Array.isArray(query.products)
    ? query.products.join(',')
    : (query.products as string);
  const urlRegions = Array.isArray(query.regions)
    ? query.regions.join(',')
    : (query.regions as string);
  const urlProductsArr = urlProducts ? urlProducts.split(',') : ['ABS'];
  const urlRegionsArray = urlRegions ? urlRegions.split(',') : ['USOA'];

  const allProductsAbbreviations = sortBy(productsCollection.map((product) => product.abbreviation));
  const allRegionsAbbreviations = sortBy(regionsCollection.map((region) => region.abbreviation));

  const urlProductsAbbreviations = allProductsAbbreviations.filter((product) => urlProductsArr.includes(product));
  const urlRegionsAbbreviations = allRegionsAbbreviations.filter((region) => urlRegionsArray.includes(region));

  let newFilters: IProductRegionFilters = {};

  const getDefaultProductsForUrl = (): string => (
    shouldSetFilterFromHeader
      ? urlProductsAbbreviations.join(',')
      : allProductsAbbreviations.join(',')
  );

  const getDefaultRegionsForUrl = (): string => (
    shouldSetFilterFromHeader
      ? urlRegionsAbbreviations.join(',')
      : allRegionsAbbreviations.join(',')
  );

  // validate ?products=... in url
  const {
    updatedQuery: validProducts,
    hasChanged: productsChanged,
  } = validateUrlFilter(urlProducts, productsCollection, getDefaultProductsForUrl());

  // validate ?regions=... in url
  const {
    updatedQuery: validRegions,
    hasChanged: regionsChanged,
  } = validateUrlFilter(urlRegions, regionsCollection, getDefaultRegionsForUrl());

  // if we should add something as the ?queryString
  if (productsChanged || regionsChanged) {
    const updatedQuery = addQueryParams(query, {
      products: validProducts,
      regions: validRegions,
    });
    newFilters.queryParam = getQueryString(updatedQuery);
  }

  const productIds = Product.getIds(validProducts);
  const regionIds = Region.getIds(validRegions);

  const {
    productIds: pageFiltersProductIds,
    regionIds: pageFiltersRegionIds,
  } = state;

  // compare what we have in URL (or want to have there) with current pageFilters
  if (!isEqual(sortBy(pageFiltersProductIds), sortBy(productIds))) {
    newFilters.productIds = productIds;
  }
  if (!isEqual(sortBy(pageFiltersRegionIds), sortBy(regionIds))) {
    newFilters.regionIds = regionIds;
  }

  // If we have URL query/store updates - apply and save them
  if (Object.keys(newFilters).length) {
    setProductRegionFilters(newFilters, pageFiltersState, 'replace');
    return true;
  }

  return false;
};

export const setProductRegionFilters = (
  filters: IProductRegionFilters,
  { state, setPageFilters }: IUsePageFiltersState,
  historyMethod: string = 'push',
  isPreventSSR: boolean = false,
  pathname?: string,
): void => {
  const currentPathname = pathname || Router.asPath?.split('?')[0] || Router.pathname;
  const withRedirect = (!!historyMethod !== false);

  let productIds = filters.productIds || state.productIds;
  let regionIds = filters.regionIds || state.regionIds;

  // set new OR update product/region
  setPageFilters({
    productIds,
    regionIds,
  });

  // redirect to the new set of filters
  if (filters.queryParam && withRedirect) {
    if (!isServer()) {
      const targetUrl = `${ currentPathname }?${ filters.queryParam }`;

      if (targetUrl === Router.asPath) {
        return;
      }

      if (historyMethod === 'replace') {
        Router.replace(targetUrl, undefined, { shallow: isPreventSSR });
      } else {
        Router.push(targetUrl, undefined, { shallow: isPreventSSR });
      }
    }
  }
};
